/**
 * Why a sponsor redemption was refused.
 *
 * `RedeemSponsorAPI` answers with a `status` code, and the codes mean very
 * different things to the student: a duplicate redemption, a membership nobody
 * has approved, a quantity that is out of range, a sponsor that has gone. Collapsing
 * them into one "Redeem failed. Please try again." is how a redemption that can
 * never succeed looks like a transient glitch — the student retries a request that
 * cannot succeed and is never told why.
 *
 * The codes live here rather than in an `else if` chain per screen because that is
 * exactly how they drifted: this app's sponsor card and the web build's redemption
 * modal each hand-rolled the chain, and this copy went no further than `-40028`. A
 * code this app forgot is a redemption refused with no explanation.
 *
 * Three groups are deliberately kept out of the table:
 *
 *   - `3/4/5/6` are the auth envelope. `service.call` has already raised its own
 *     alert carrying the real reason, so re-explaining it inline double-alerts.
 *   - `2` and `2003` mean our own request was malformed, which is our bug and not
 *     the student's: neutral copy, with the detail left in the log.
 *   - Anything unrecognised falls through to the server's own message, and to the
 *     log if there is not one.
 */

/**
 * Envelope statuses `service.call` already surfaces itself.
 *
 * The transport helper pops a native alert and logs the raw reason for these
 * before handing the body back, so they are never a screen's job to re-explain.
 */
export const AUTH_ENVELOPE_STATUSES: readonly number[] = [3, 4, 5, 6];

export const isAuthEnvelopeStatus = (status?: number | null): boolean =>
  AUTH_ENVELOPE_STATUSES.includes(status as number);

/**
 * Statuses that are our fault rather than the student's.
 *
 * Kept separate from the message table so the copy stays neutral while the log
 * keeps the detail: telling a student "your quantity is invalid" when the server
 * rejected the shape of our request sends them to fix something that is fine.
 */
export const isServerFaultStatus = (status?: number | null): boolean =>
  status === 2 || status === 2003;

/** Shown when the server refuses without explaining, or we never reached it. */
export const GENERIC_REDEEM_FAILURE = 'Redeem failed. Please try again.';

/** Shown when the failure is ours, so the student is not asked to change anything. */
export const SERVER_FAULT_REDEEM_FAILURE = 'Something went wrong on our side. Please try again.';

/**
 * Status → translation key. Keys, not prose, so each platform resolves them
 * through its own catalogue.
 */
const MESSAGE_KEYS: Readonly<Record<number, string>> = {
  [-40005]: 'You can only redeem this sponsor once.',
  [-30078]: 'Society membership not verified yet. Please wait for a society member to approve.',
  [-30065]: 'Personal verification pending. Please wait for UninfoHK to verify your documents.',
  [-30066]: 'Please upload both your HKID and student card images.',
  [-30061]: 'Only students can redeem coupons',
  [-40009]: "Can't get sponsor tasks. Please contact support.",
  [-40022]: 'Sponsor redemption limit reached. Please try another sponsor.',
  [-40019]: 'Sponsor not found or has been removed.',
  [-40026]: 'Missing or invalid participants quantity.',
  [-40027]: 'Participants quantity is out of range.',
  [-40028]: 'Redemption quantity is invalid.',
  [-40040]: 'At least 4 available sponsor tasks are required.',
};

/** Every status with its own copy, for the parity test to walk. */
export const REDEEM_SPONSOR_MESSAGE_KEYS = MESSAGE_KEYS;

/**
 * The copy for a status, or null when this status has none of its own.
 *
 * Null is a meaningful answer: it means the caller should log the response and
 * fall back rather than inventing something.
 */
export const redeemSponsorMessageKey = (status?: number | null): string | null =>
  status != null && status in MESSAGE_KEYS ? MESSAGE_KEYS[status] : null;

/**
 * The server's own refusal text, when it carries one.
 *
 * Guards on `status` being present so the transport layer's `network error`
 * placeholder (which has `msg` and no `status`) cannot masquerade as a server
 * explanation, and collapses whitespace so a blank body never wins over real copy.
 */
export const redeemSponsorServerMessage = (resp: any): string | null => {
  const message = typeof resp?.message === 'string' ? resp.message.trim() : '';
  return message !== '' ? message : null;
};

/**
 * What to show for a failed redemption, given the whole response.
 *
 * Prefers our own mapped copy, then the server's message, then the generic
 * fallback — so an unmapped status is still explained rather than swallowed. The
 * return is a translation key when the copy is ours and a literal when it is the
 * server's, which is why callers pass it straight to `t`; `t` returns its argument
 * unchanged for a string it has no entry for.
 */
export const redeemSponsorFailureKey = (resp: any): string => {
  const status = resp?.status;
  if (isServerFaultStatus(status)) return SERVER_FAULT_REDEEM_FAILURE;
  return (
    redeemSponsorMessageKey(status) ??
    redeemSponsorServerMessage(resp) ??
    GENERIC_REDEEM_FAILURE
  );
};
